// Support: de gedeelde mailbox (SUPPORT_MAILBOX, ediflex@tlcc.be), alleen lezen en alleen metadata, met de markup van
// het prototype (supRoot in design/build/prototype.js, logica in proto-support-logic.js): vier kerncijfers, een raster
// per developer of per klant en per week of maand (filter, sortering, inzichten), en rechts de open gesprekken met
// tabbladen en zoeken. Een gesprek opent een lade met het verloop en Openen in Outlook.
// Een categorie in Outlook is de developer, de map Archief is afgehandeld, de telling is per gesprek. De klant uit de
// bovenbalk beperkt alles tot die klant (de server filtert). De planner koppelt hier een onbekend afzenderdomein aan
// een klant; de medewerker ziet hetzelfde zonder koppelen, en neemt zelf op in Outlook.
// Nog niet: "Wie kan dit opnemen" (capaciteit uit Clockify) en bouwverlof in het raster.
import { useState } from "react";
import { useSearchParams } from "react-router";
import { useSupport, type SupportSync } from "../lib/api-support";
import { useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays, ddmm, firstName, initials } from "../lib/planning";
import { devLabel, durText, fmtNum, hhmm, hoursSince, kInit, klantText, MONTHS, nOf, sum, waitCls, waitText, when } from "../lib/support";
import { DomainLinkDrawer } from "../components/DomainLinkDrawer";
import { SupportConvDrawer } from "../components/SupportConvDrawer";
import { Av } from "../components/Avatar";
import { useAppState } from "../shell/AppState";
import { LoadError } from "../shell/Layout";

type Tab = "wacht" | "open" | "alles";
type Sort = "meest" | "stijgend" | "open" | "naam";
const SORTS: [Sort, string][] = [["meest", "Meeste support"], ["stijgend", "Sterkst stijgend"], ["open", "Meeste open gesprekken"], ["naam", "Naam"]];
// één blauwe schaal, gemeten per week (in een maand: gemiddeld per week); nul is een lege cel
const BIN_LABELS = ["1–2", "3–4", "5–6", "7–9", "10+"];
const binOf = (v: number, per: number) => (v <= 0 ? "z" : per >= 10 ? 4 : per >= 7 ? 3 : per >= 5 ? 2 : per >= 3 ? 1 : 0);

interface Col { idx: number[]; label: string; sub: string; long: string; weeks: number }
interface Row { key: string; label: string; full: string; init: string; pid?: string | null; sq: boolean; sub: string; cells: number[]; total: number; open: number }

/** stijgend: gemiddelde van de laatste 3 weken tegenover de weken ervoor */
function trend(cells: number[]) {
  const n = cells.length, last = cells.slice(n - 3), before = cells.slice(0, n - 3);
  const a = sum(last) / 3, b = sum(before) / Math.max(1, before.length);
  return { a, b, ratio: b ? a / b : a ? 9 : 0 };
}

function SyncNotice({ s, med }: { s: SupportSync; med: boolean }) {
  const since = s.lastOk ? " U ziet de gegevens van " + when(s.lastOk) + "." : "";
  if (s.state === "uit") return <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>De synchronisatie met de supportmailbox staat uit.</p></div>;
  if (s.state === "nooit" || s.state === "toegang") {
    const how = med ? " Zodra de planning ze kan lezen, verschijnen de gesprekken hier."
      : s.state === "toegang" ? " De app heeft nog geen leestoegang tot " + s.mailbox + ". Een Exchange-beheerder geeft die met het script support-mailbox-toegang.ps1; daarna verschijnen de gesprekken hier binnen enkele minuten."
      : " De eerste lezing van " + s.mailbox + " volgt binnen enkele minuten.";
    return (
      <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} />
        <p><b>De supportmailbox is nog niet gekoppeld.</b>{how}{since}{!med && s.error ? " Laatste fout: " + s.error : ""}</p>
      </div>
    );
  }
  if (s.state === "fout") {
    return (
      <div className="ex-notice ex-notice--warning p-notice"><Icon name="alert" size={16} />
        <p>De laatste synchronisatie met {s.mailbox} mislukte{s.errorAt ? " (" + when(s.errorAt) + ")" : ""}: {s.error}.{since}</p>
      </div>
    );
  }
  if (s.busy) return <div className="ex-notice ex-notice--mist p-notice"><Icon name="info" size={16} /><p>De eerste lezing van {s.mailbox} loopt nog: de historiek vult zich de komende minuten aan.</p></div>;
  return null;
}

export function Support() {
  const { klant: kf, setKlant } = useAppState();
  const { data, error, refetch } = useSupport(kf);
  const { data: me } = useMe();
  const { data: planning } = usePlanning();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>("wacht");
  const [dev, setDev] = useState<string | null>(null);
  const [klantSel, setKlantSel] = useState<string | null>(null);
  const [gran, setGran] = useState<"week" | "maand">("week");
  const [rowsBy, setRowsBy] = useState<"dev" | "klant">("dev");
  const [sort, setSort] = useState<Sort>("meest");
  const [ppOpen, setPpOpen] = useState(false);
  const [srtOpen, setSrtOpen] = useState(false);
  const [hideDev, setHideDev] = useState<Record<string, boolean>>({});
  const [hideKlant, setHideKlant] = useState<Record<string, boolean>>({});
  const [ppQ, setPpQ] = useState("");
  const [q, setQ] = useState("");
  const [dom, setDom] = useState<{ domain: string | null } | null>(null);

  if (!data && error) return <LoadError what="Het supportoverzicht" onRetry={() => refetch()} />;
  if (!data || !me) return <div className="p-loading">Supportgesprekken laden…</div>;
  const med = me.role === "medewerker";
  const isK = rowsBy === "klant";
  const mb = data.sync.mailbox || "de supportmailbox";
  const now = Date.now();
  const waitH = (iso: string) => hoursSince(iso, now);

  const openAll = data.open;
  const waiting = openAll.filter((s) => !s.devs.length), busy = openAll.filter((s) => s.devs.length);
  const oldest = waiting.slice().sort((a, b) => Date.parse(a.openSince) - Date.parse(b.openSince))[0];
  const busyDevs = new Set(busy.flatMap((s) => s.devs.map((d) => d.key)));

  // kolommen: 12 weken, of per maand (de weken van die maand samen)
  const cols: Col[] = gran === "week"
    ? data.weeks.map((w, i) => ({ idx: [i], label: String(w.nr), sub: "", long: "week " + w.nr + ", " + ddmm(w.monday) + " tot " + ddmm(addDays(w.monday, 4)), weeks: 1 }))
    : data.weeks.reduce<(Col & { m: number })[]>((out, w, i) => {
      const m = Number(w.monday.slice(5, 7)) - 1;
      let c = out.find((x) => x.m === m);
      if (!c) { c = { m, idx: [], label: MONTHS[m], sub: "", long: MONTHS[m], weeks: 0 }; out.push(c); }
      c.idx.push(i); c.weeks++; c.sub = nOf(c.weeks, "week", "weken");
      return out;
    }, []);

  // rijen: developers (een categorie zonder persoon blijft als tekst) of klanten (zonder klant = onbekend of genegeerd)
  const firsts = data.devs.filter((d) => d.personId).map((d) => firstName(d.name));
  const all: Row[] = isK
    ? data.klanten.map((k) => ({
      key: k.key, label: k.key ? k.name : "Zonder klant", full: k.key ? k.name : "Zonder klant", init: kInit(k.key ? k.name : ""), sq: true,
      sub: (k.key ? k.domains.join(", ") || "geen domein" : nOf(data.unknownDomains.length, "onbekend domein", "onbekende domeinen")) + (k.open ? " · " + k.open + " open" : ""),
      cells: k.cells, total: k.total, open: k.open,
    }))
    : data.devs.map((d) => ({
      key: d.key, label: d.personId && firsts.filter((f) => f === firstName(d.name)).length === 1 ? firstName(d.name) : d.name, full: d.name, init: initials(d.name), pid: d.personId, sq: false,
      sub: d.personId ? (d.open ? nOf(d.open, "open gesprek", "open gesprekken") : "geen open gesprekken") : "categorie zonder persoon" + (d.open ? " · " + d.open + " open" : ""),
      cells: d.cells, total: d.total, open: d.open,
    }));
  const grand = data.grand;
  const share = (r: Row) => (grand ? Math.round((r.total / grand) * 100) : 0);
  const tr = new Map(all.map((r) => [r.key, trend(r.cells)]));
  const maxShare = Math.max(1, ...all.map(share));
  const hide = isK ? hideKlant : hideDev, setHide = isK ? setHideKlant : setHideDev;
  const byName = (a: Row, b: Row) => a.label.localeCompare(b.label);
  const sorter: Record<Sort, (a: Row, b: Row) => number> = {
    meest: (a, b) => b.total - a.total || byName(a, b),
    stijgend: (a, b) => tr.get(b.key)!.ratio - tr.get(a.key)!.ratio || b.total - a.total,
    open: (a, b) => b.open - a.open || b.total - a.total,
    naam: byName,
  };
  const shown = all.filter((r) => !hide[r.key]).sort(sorter[sort]);
  const rising = (r: Row) => tr.get(r.key)!.ratio >= 1.6 && r.total >= 6;
  const teamRow = cols.map((c) => sum(c.idx.map((i) => data.team[i])));

  // inzichten: het grootste aandeel en de sterkste stijger
  const top = grand ? all.slice().sort((a, b) => b.total - a.total)[0] : undefined;
  const riser = all.filter((r) => rising(r) && r.key !== "").sort((a, b) => tr.get(b.key)!.ratio - tr.get(a.key)!.ratio)[0];
  const insights: { text: string; cls: string }[] = [];
  if (top) insights.push({ text: isK ? top.label + ": " + share(top) + " % van alle support" : top.label + " nam " + share(top) + " % van alle support op", cls: "p-ins" });
  if (riser) insights.push({ text: riser.label + ": " + fmtNum(tr.get(riser.key)!.a) + " per week in de laatste 3 weken, was " + fmtNum(tr.get(riser.key)!.b), cls: "p-ins warn" });
  const unknownN = data.klanten.find((k) => k.key === "")?.total || 0;
  const hasUnknown = isK && data.unknownDomains.length > 0;
  const unknownText = (unknownN ? nOf(unknownN, "gesprek", "gesprekken") + " zonder klant" : nOf(data.unknownDomains.length, "onbekend domein", "onbekende domeinen")) + (med ? "" : " · koppelen");

  // filter op personen of klanten
  const pq = ppQ.trim().toLowerCase();
  const items = all.slice().sort(byName).filter((r) => !pq || r.full.toLowerCase().includes(pq));
  const what = isK ? "Klanten" : "Personen";

  // open gesprekken
  const devName = (key: string) => { const r = data.devs.find((d) => d.key === key); return r ? devLabel(r) : key; };
  const klantName = (key: string) => (key === "" ? "zonder klant" : data.klanten.find((k) => k.key === key)?.name || planning?.klanten.find((k) => k.id === key)?.name || "deze klant");
  let list = tab === "wacht" ? waiting : tab === "open" ? busy : openAll;
  if (dev) list = list.filter((s) => s.devs.some((d) => d.key === dev));
  if (klantSel !== null) list = list.filter((s) => (s.klantId || "") === klantSel);
  const cq = q.trim().toLowerCase();
  if (cq) list = list.filter((s) => [s.subject, klantText(s), s.domain, s.devs.map((d) => d.name + " " + d.category).join(" ") || "niet opgenomen", s.from].join(" ").toLowerCase().includes(cq));
  const conv = list.slice().sort((a, b) => (a.devs.length ? 1 : 0) - (b.devs.length ? 1 : 0) || Date.parse(a.openSince) - Date.parse(b.openSince));
  const filterText = dev ? "Alleen " + devName(dev) : klantSel !== null ? "Alleen " + klantName(klantSel) : kf ? "Alleen " + klantName(kf) : "";
  const clearFilter = () => { setDev(null); setKlantSel(null); if (!dev && klantSel === null) setKlant(null); };
  const pickKpi = (t: Tab) => { setTab(t); setDev(null); setKlantSel(null); setQ(""); };

  const sel = params.get("gesprek");
  const selConv = sel ? openAll.find((s) => s.id === sel) : null;
  const openConv = (id: string) => setParams((p) => { p.set("gesprek", id); return p; });
  const closeConv = () => setParams((p) => { p.delete("gesprek"); return p; });
  const myCategory = data.devs.find((d) => d.personId === me.id)?.category || firstName(me.name);
  const lastW = data.team[data.team.length - 1] || 0;

  const kpis = [
    { dot: "err", label: "Wacht op een developer", value: String(waiting.length), sub: oldest ? "oudste " + waitText(waitH(oldest.openSince)) + " · " + klantText(oldest) : "niemand wacht", tab: "wacht" as Tab, cls: waiting.length ? " err" : "" },
    { dot: "warn", label: "In behandeling", value: String(busy.length), sub: "verdeeld over " + nOf(busyDevs.size, "developer", "developers"), tab: "open" as Tab, cls: "" },
    { dot: "ok", label: "Afgehandeld · 12 weken", value: String(grand), sub: lastW + " deze week · gemiddeld " + fmtNum(grand / Math.max(1, data.weeks.length)), tab: "alles" as Tab, cls: "" },
  ];
  const lead = data.lead;
  const leadSub = lead.nPick ? "opnemen " + durText(lead.pick) + " · afwerken " + durText(lead.done) : lead.n ? "van ontvangen tot Archief" : "nog geen afgehandelde gesprekken";

  return (
    <>
      <div className="p-head">
        <div><h1>Support</h1><p>Gedeelde mailbox {mb} · een categorie is de developer · de map Archief is afgehandeld · telling per gesprek</p></div>
        <div className="p-tools">
          <div className="ex-seg" role="group" aria-label="Per week of per maand">
            <button type="button" aria-pressed={gran === "week"} onClick={() => setGran("week")}>Per week</button>
            <button type="button" aria-pressed={gran === "maand"} onClick={() => setGran("maand")}>Per maand</button>
          </div>
        </div>
      </div>
      <SyncNotice s={data.sync} med={med} />
      <div className="p-kpis">
        {kpis.map((k) => (
          <button key={k.label} type="button" className={"p-kpi" + k.cls + (tab === k.tab && k.tab !== "alles" ? " on" : "")} onClick={() => pickKpi(k.tab)}>
            <span className="l"><span className={"ex-dot ex-dot--" + k.dot} />{k.label}</span><span className="v">{k.value}</span><span className="s">{k.sub}</span>
          </button>
        ))}
        <div className="p-kpi fixed" title={lead.n ? "Mediaan over " + nOf(lead.n, "afgehandeld gesprek", "afgehandelde gesprekken") + " in 12 weken" : undefined}>
          <span className="l"><span className="ex-dot ex-dot--off" />Doorlooptijd</span><span className="v">{durText(lead.total)}</span><span className="s">{leadSub}</span>
        </div>
      </div>
      <div className="p-supg">
        <div className="p-sup-l">
          <section className="p-card p-vis p-hm">
            <div className="p-card-h">
              <div><h2>{isK ? "Support per klant" : "Wie nam support op"}</h2><p>{isK ? "Afgehandelde gesprekken per klant volgens het afzenderdomein" : "Afgehandelde gesprekken per developer"} · klik op een rij om de open gesprekken te filteren</p></div>
              <div className="p-ins-row">
                {insights.map((x) => <span key={x.text} className={x.cls}>{x.text}</span>)}
                {hasUnknown && (med
                  ? <span className="p-ins warn">{unknownText}</span>
                  : <button type="button" className="p-ins warn p-ins-a" onClick={() => setDom({ domain: data.unknownDomains.length === 1 ? data.unknownDomains[0].domain : null })}>{unknownText}</button>)}
              </div>
            </div>
            <div className="p-hbar">
              <div className="ex-seg" role="group" aria-label="Rijen">
                <button type="button" aria-pressed={!isK} onClick={() => { setRowsBy("dev"); setPpOpen(false); setSrtOpen(false); }}>Developers</button>
                <button type="button" aria-pressed={isK} onClick={() => { setRowsBy("klant"); setPpOpen(false); setSrtOpen(false); }}>Klanten</button>
              </div>
              <div className="p-ddwrap">
                <button type="button" className="ex-btn p-dd" aria-haspopup="true" aria-expanded={ppOpen} onClick={() => { setPpOpen(!ppOpen); setSrtOpen(false); setPpQ(""); }}>{what}<span className="p-dd-v">{shown.length === all.length ? "alle " + all.length : shown.length + " van " + all.length}</span><Icon name="down" size={14} /></button>
                {ppOpen && <>
                  <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setPpOpen(false)} />
                  <div className="p-pop p-pop-m" role="dialog" aria-label={isK ? "Klanten" : "Developers"}>
                    <div className="p-pop-h"><span>{isK ? "Klanten" : "Developers"}</span>
                      <span className="p-pop-acts">
                        <button type="button" className="ex-link" onClick={() => setHide({})}>{isK ? "Alle" : "Iedereen"}</button>
                        <button type="button" className="ex-link" onClick={() => setHide(Object.fromEntries(all.filter((r) => !r.open).map((r) => [r.key, true])))}>Met open gesprekken</button>
                        <button type="button" className="ex-link" onClick={() => setHide(Object.fromEntries(all.map((r) => [r.key, true])))}>Wissen</button>
                      </span>
                    </div>
                    {all.length > 8 && <div className="p-pop-q"><label className="ex-search"><Icon name="search" size={14} /><input type="text" placeholder="Zoek in de lijst" aria-label="Zoek in de lijst" value={ppQ} onChange={(e) => setPpQ(e.target.value)} /></label></div>}
                    <div className="p-pop-b">
                      {items.map((r) => (
                        <label key={r.key} className={"p-opt s" + (hide[r.key] ? "" : " on")}>
                          <input type="checkbox" checked={!hide[r.key]} onChange={() => setHide({ ...hide, [r.key]: !hide[r.key] })} />
                          <Av id={r.pid} init={r.init} cls={"p-av s" + (r.sq ? " sq" : "")} /><span className="nm">{r.full}</span><span className="c">{r.total}</span>
                        </label>
                      ))}
                      {!items.length && <p className="p-empty p-pop-empty">Niets gevonden.</p>}
                    </div>
                    <div className="p-pop-f">Totaal afgehandeld in 12 weken · de teamrij telt altijd iedereen</div>
                  </div>
                </>}
              </div>
              <div className="p-ddwrap">
                <button type="button" className="ex-btn p-dd" aria-haspopup="true" aria-expanded={srtOpen} onClick={() => { setSrtOpen(!srtOpen); setPpOpen(false); }}>Sorteren<span className="p-dd-v">{SORTS.find((x) => x[0] === sort)![1]}</span><Icon name="down" size={14} /></button>
                {srtOpen && <>
                  <button type="button" className="p-dd-scrim" aria-label="Sluiten" onClick={() => setSrtOpen(false)} />
                  <div className="p-pop p-pop-s" role="dialog" aria-label="Sorteren">
                    <div className="p-pop-b">
                      {SORTS.map(([k, label]) => (
                        <button key={k} type="button" className={"p-opt1" + (sort === k ? " on" : "")} onClick={() => { setSort(k); setSrtOpen(false); }}><span className={"p-radio" + (sort === k ? " on" : "")} />{label}</button>
                      ))}
                    </div>
                  </div>
                </>}
              </div>
            </div>
            <div className="p-hscroll">
              <div className={"p-hgrid " + (gran === "week" ? "wk" : "mo" + (cols.length === 4 ? " mo4" : ""))}>
                <div className="p-hmr p-hhead">
                  <span className="p-hn">{(isK ? "Klant" : "Developer") + (gran === "week" ? " · week" : "")}</span>
                  {cols.map((c) => <span key={c.label} className="p-hh">{c.label}<small>{c.sub}</small></span>)}
                  <span className="p-htot">Totaal</span><span className="p-hshare">Aandeel</span>
                </div>
                {shown.map((r, ri) => {
                  const on = isK ? klantSel === r.key : dev === r.key;
                  const pick = () => {
                    if (isK) { setKlantSel(on ? null : r.key); setDev(null); } else { setDev(on ? null : r.key); setKlantSel(null); }
                    setTab("alles");
                  };
                  return (
                    <button key={r.key} type="button" className={"p-hmr" + (on ? " on" : "")} onClick={pick}>
                      <span className="p-hn"><Av id={r.pid} init={r.init} cls={"p-av s" + (r.sq ? " sq" : "")} /><span className="t"><b>{r.label}</b><small>{r.sub}{rising(r) && <em className="p-hrise">stijgt</em>}</small></span></span>
                      {cols.map((c) => {
                        const v = sum(c.idx.map((i) => r.cells[i])), per = v / c.weeks;
                        return (
                          <span key={c.label} className={"p-hc b" + binOf(v, per)}>{v || ""}
                            <span className={"p-tip" + (ri < 2 ? " below" : "")} role="tooltip">
                              <b>{(v ? nOf(v, "gesprek", "gesprekken") : "geen gesprekken") + " afgehandeld" + (c.weeks > 1 && v ? ", gemiddeld " + fmtNum(per) + " per week" : "")}</b>
                              <span>{r.label}, {c.long}</span>
                            </span>
                          </span>
                        );
                      })}
                      <b className="p-htot">{r.total}</b>
                      <span className="p-hshare"><span className="p-hsb"><i style={{ width: Math.round((share(r) / maxShare) * 100) + "%" }} /></span><span>{share(r)} %</span></span>
                    </button>
                  );
                })}
                {!shown.length && <p className="p-empty p-hempty">{!all.length ? "Nog geen afgehandelde of open gesprekken in deze 12 weken." : isK ? "Geen klant geselecteerd. Kies via Klanten welke u wilt zien." : "Niemand geselecteerd. Kies via Personen wie u wilt zien."}</p>}
                <div className="p-hmr p-hteam">
                  <span className="p-hn"><span className="t"><b>{isK ? "Alle klanten" : "Team"}</b><small>{nOf(all.length, isK ? "klant" : "developer", isK ? "klanten" : "developers") + (shown.length < all.length ? ", " + shown.length + " in beeld" : "")}</small></span></span>
                  {teamRow.map((v, i) => <b key={i} className="p-hc-t">{v}</b>)}
                  <b className="p-htot">{grand}</b><span className="p-hshare" />
                </div>
              </div>
            </div>
            <div className="p-hleg">
              <span>Minder</span><span className="p-hlegi"><i className="p-hsw bz" />0</span>
              {BIN_LABELS.map((l, i) => <span key={l} className="p-hlegi"><i className={"p-hsw b" + i} />{l}</span>)}
              <span>Meer · {gran === "week" ? "gesprekken per week" : "gemiddeld per week in die maand"}</span>
            </div>
          </section>
        </div>
        <section className="p-card p-sup-r">
          <div className="p-card-h">
            <div><h2>Open gesprekken</h2><p>In de inbox van {mb}{data.sync.lastOk ? " · bijgewerkt " + hhmm(data.sync.lastOk) : ""}</p></div>
            {filterText && <button type="button" className="p-filterchip" onClick={clearFilter}>{filterText}<Icon name="x" size={12} /></button>}
          </div>
          <div className="p-mw-tabs">
            <div className="ex-tabs p-tabs" role="tablist">
              {([["wacht", "Wacht", waiting.length], ["open", "In behandeling", busy.length], ["alles", "Alles", openAll.length]] as [Tab, string, number][]).map(([k, label, n]) => (
                <button key={k} type="button" role="tab" className="ex-tab" aria-selected={tab === k} onClick={() => setTab(k)}>{label}<span className="p-tabn">{n}</span></button>
              ))}
            </div>
          </div>
          <div className="p-csearch"><label className="ex-search"><Icon name="search" size={15} /><input type="text" placeholder="Zoek onderwerp, klant of developer" aria-label="Zoek in de open gesprekken" value={q} onChange={(e) => setQ(e.target.value)} /></label></div>
          <div className="p-conv">
            {conv.map((s) => {
              const h = waitH(s.openSince);
              return (
                <button key={s.id} type="button" className="p-crow" onClick={() => openConv(s.id)}>
                  {s.devs.length ? <Av id={s.devs[0].personId} init={initials(s.devs[0].name)} /> : <span className="p-av none"><Icon name="userx" size={14} /></span>}
                  <span className="t"><b>{s.subject}</b>
                    <span className={s.devs.length ? "" : "t-warn"}>{(s.devs.length ? s.devs.map(devLabel).join(", ") : "niet opgenomen") + " · " + klantText(s) + " · " + nOf(s.messages, "bericht", "berichten")}</span>
                  </span>
                  <span className={"p-cwait " + waitCls(h)}>{h >= 48 && <Icon name="alert" size={13} />}{waitText(h)}</span>
                </button>
              );
            })}
            {!conv.length && <p className="p-empty p-mw-empty">{cq ? "Niets gevonden voor " + q.trim() + "." : "Geen open gesprekken met deze keuze."}</p>}
          </div>
          <div className="p-card-f"><span>Opnemen en archiveren gebeurt in Outlook; deze pagina leest alleen.</span></div>
        </section>
      </div>
      {selConv && <SupportConvDrawer key={selConv.id} conv={selConv} mailbox={mb} med={med} myCategory={myCategory} onClose={closeConv}
        onLink={med ? undefined : (d) => { closeConv(); setDom({ domain: d }); }} />}
      {dom && !med && <DomainLinkDrawer start={dom.domain} unknown={data.unknownDomains} klanten={planning?.klanten || []} onClose={() => setDom(null)} />}
    </>
  );
}
